import React from 'react';

import { Divider, Drawer, Typography } from 'antd';
import type { Moment } from 'moment';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { InfoCardList } from '@src/components/InfoCard';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { DateUtil } from '@src/packages/utils/dates';
import WorkJourneyState from '@src/packages/workJourney/components/WorkJourneyState';
import { WORK_JOURNEY_PLACE_TYPE } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyAssignedUser, WorkJourneyUserSummary } from '@src/packages/workJourney/types';
import IntlMessages from '@src/util/IntlMessages';

import styles from './styles.module.less';

const { Text } = Typography;

const EMPTY_DISPLAY = '-';

interface WorkJourneyInfoProps {
  isVisible: boolean;
  onCancel: () => void;
}

interface ReadonlyFieldProps {
  label: React.ReactNode;
  value: React.ReactNode;
}

const formatBreakTime = (value: Moment | string | null | undefined): string => {
  if (value == null) return EMPTY_DISPLAY;
  if (typeof value === 'string') return value || EMPTY_DISPLAY;
  return value.format('HH:mm');
};

const formatDate = (ts: unknown) => {
  const formatted = DateUtil.toDateString(ts, 'DD/MM/YYYY');
  return formatted || EMPTY_DISPLAY;
};

const displayValue = (value: string | number | null | undefined): React.ReactNode => {
  if (value == null || value === '') {
    return <span className={styles.emptyValue}>{EMPTY_DISPLAY}</span>;
  }

  return value;
};

const getUserDisplayName = (user: WorkJourneyUserSummary | undefined): string => {
  if (!user) return EMPTY_DISPLAY;

  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.email || EMPTY_DISPLAY;
};

const getUserKey = (user: WorkJourneyUserSummary | undefined, index: number): string =>
  user?.id ?? user?.email ?? `user-${index}`;

const ReadonlyField = ({ label, value }: ReadonlyFieldProps) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <div className={styles.readonlyField}>{value}</div>
  </div>
);

const AssignedUserCards = ({ users }: { users: WorkJourneyAssignedUser[] }) => (
  <div className={styles.userList}>
    {users.map(({ user }, index) => (
      <InfoCardList
        key={getUserKey(user, index)}
        title={<Text>{getUserDisplayName(user)}</Text>}
        avatar={<MemberAvatar user={user} size={36} />}
      />
    ))}
  </div>
);

const WorkJourneyInfo = ({ isVisible, onCancel }: WorkJourneyInfoProps) => {
  const { selectedWorkJourney } = useWorkJourneyContext();

  const toleranceMinutes =
    selectedWorkJourney?.tolerenceTime ?? selectedWorkJourney?.toleranceTime;
  const members = selectedWorkJourney?.members ?? [];
  const supervisors = selectedWorkJourney?.supervisors ?? [];

  return (
    <Drawer
      open={isVisible}
      onClose={onCancel}
      width={720}
      closable={false}
      footer={null}
      title={null}
      destroyOnClose
      className={styles.drawer}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onCancel}
            title={
              <div className={styles.headerTitle}>
                <Title.Header value={selectedWorkJourney?.name || ''} />
                {selectedWorkJourney && <WorkJourneyState workJourney={selectedWorkJourney} />}
              </div>
            }
          />
        </BoxContainer>

        <BoxContainer content className={styles.wrapper}>
          {selectedWorkJourney && (
            <>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.associatedShift" />
                </h3>

                <ReadonlyField
                  label={<IntlMessages id="workJourney.management.plan.fields.shift" />}
                  value={displayValue(selectedWorkJourney.workShift?.name)}
                />

                <div className={styles.doubleField}>
                  <ReadonlyField
                    label={<IntlMessages id="workJourney.shift.startDate" />}
                    value={formatDate(selectedWorkJourney.shiftStartDate)}
                  />
                  <ReadonlyField
                    label={<IntlMessages id="workJourney.shift.endDate" />}
                    value={
                      selectedWorkJourney.shiftEndDate
                        ? formatDate(selectedWorkJourney.shiftEndDate)
                        : displayValue(null)
                    }
                  />
                </div>
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.collation.title" />
                </h3>

                <Text type="secondary" className={styles.helperText}>
                  <IntlMessages id="workJourney.collation.hint" />
                </Text>

                <ReadonlyField
                  label={<IntlMessages id="workJourney.collation.method" />}
                  value={
                    selectedWorkJourney.breakMarkingType === 'automatic' ? (
                      <IntlMessages id="workJourney.collation.automatic" />
                    ) : (
                      <IntlMessages id="workJourney.collation.manual" />
                    )
                  }
                />

                <ReadonlyField
                  label={<IntlMessages id="workJourney.breakStartTime" />}
                  value={formatBreakTime(selectedWorkJourney.breakStartTime)}
                />
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.location" />
                </h3>

                <ReadonlyField
                  label={<IntlMessages id="workJourney.locationDescription" />}
                  value={
                    selectedWorkJourney.locationType === WORK_JOURNEY_PLACE_TYPE.PRESENTIAL ? (
                      <IntlMessages id="workJourney.presential" />
                    ) : selectedWorkJourney.locationType === WORK_JOURNEY_PLACE_TYPE.REMOTE ? (
                      <IntlMessages id="workJourney.remote" />
                    ) : (
                      <IntlMessages id="workJourney.withoutLocation" />
                    )
                  }
                />

                <ReadonlyField
                  label={<IntlMessages id="workJourney.selectedLocation" />}
                  value={displayValue(selectedWorkJourney.address?.address)}
                />
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.scheduleTolerance" />
                </h3>

                <Text type="secondary" className={styles.helperText}>
                  <IntlMessages id="workJourney.toleranceDescription" />
                </Text>

                <ReadonlyField
                  label={<IntlMessages id="workJourney.allowedTime" />}
                  value={
                    toleranceMinutes != null ? (
                      <>
                        {toleranceMinutes} <IntlMessages id="workJourney.minutes" />
                      </>
                    ) : (
                      displayValue(null)
                    )
                  }
                />
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.earlyTerminationJourney" />
                </h3>

                <ReadonlyField
                  label={<IntlMessages id="workJourney.earlyTerminationDescription" />}
                  value={
                    selectedWorkJourney.earlyEndTime != null ? (
                      <>
                        {selectedWorkJourney.earlyEndTime}{' '}
                        <IntlMessages id="workJourney.minutes" />
                      </>
                    ) : (
                      displayValue(null)
                    )
                  }
                />
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.assignedSupervisors" />
                </h3>

                {supervisors.length === 0 ? (
                  <div className={styles.readonlyField}>
                    <IntlMessages id="workJourney.noSupervisorsAssigned" />
                  </div>
                ) : (
                  <AssignedUserCards users={supervisors} />
                )}
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.requiredUsers" />
                </h3>

                <div className={styles.readonlyField}>{selectedWorkJourney.requiredMembers ?? 0}</div>
              </section>

              <Divider className={styles.divider} />

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <IntlMessages id="workJourney.assignedUsers" />
                </h3>

                {members.length === 0 ? (
                  <div className={styles.readonlyField}>
                    <IntlMessages id="workJourney.noUsersAssigned" />
                  </div>
                ) : (
                  <AssignedUserCards users={members} />
                )}
              </section>
            </>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default WorkJourneyInfo;
